// dispatch-workflow-handlers.ts — 27 workflow event dispatch handlers.
//
// Wire shape: snake_case pass-through (Q7 LOCKED, confidence 100%).
// hub frame.data = json.RawMessage byte-exact from outbox row (hub.go:208).
// Handlers read data.run_id, data.step_id — NO snake→camel transform.
//
// token_delta: Replayable=false (X7 amendment); NO RQ invalidate (too noisy).
// Tracked via module-level lastSeq Map; out-of-order/dup frames dropped.
// Cleared on step.text_complete for same step; cleared on run terminal events.
import type { QueryClient } from "@tanstack/react-query";
import type { Frame } from "./types";
import { workflowFrameBus } from "./workflow-frame-bus";

// ── helpers ───────────────────────────────────────────────────────────────────

function invalidate(qc: QueryClient, ...queryKey: unknown[]) {
  qc.invalidateQueries({ queryKey });
}

function getRunId(data: Record<string, unknown> | null): string | null {
  return typeof data?.run_id === "string" ? data.run_id : null;
}

function getWorkflowId(data: Record<string, unknown> | null): string | null {
  return typeof data?.workflow_id === "string" ? data.workflow_id : null;
}

function getStepKey(runId: string, stepId: string): string {
  return `${runId}:${stepId}`;
}

// ── token_delta seq tracking ──────────────────────────────────────────────────
// Module-level map: key = "runId:stepId", value = last seen seq (bigint).
// Cleared on step.text_complete + run terminal events (completed/failed/cancelled).
// Cap at 1000 entries via FIFO eviction (insertion-order); oldest entry dropped first.

const TOKEN_DELTA_SEQ_CAP = 1000;
const lastSeq: Map<string, bigint> = new Map();

function evictTokenDeltaSeqIfNeeded(): void {
  if (lastSeq.size > TOKEN_DELTA_SEQ_CAP) {
    // Delete oldest entry (Map preserves insertion order).
    const firstKey = lastSeq.keys().next().value;
    if (firstKey !== undefined) lastSeq.delete(firstKey);
  }
}

function clearTokenDeltaSeqForStep(runId: string, stepId: string): void {
  lastSeq.delete(getStepKey(runId, stepId));
}

function clearTokenDeltaSeqForRun(runId: string): void {
  for (const key of lastSeq.keys()) {
    if (key.startsWith(`${runId}:`)) lastSeq.delete(key);
  }
}

// ── invalidate helpers for run + events keys ──────────────────────────────────

function invalidateRun(qc: QueryClient, runId: string): void {
  invalidate(qc, "workflows", "run", runId);
  invalidate(qc, "workflows", "run", runId, "events");
}

function invalidateWorkflowRuns(qc: QueryClient, data: Record<string, unknown> | null): void {
  const workflowId = getWorkflowId(data);
  if (workflowId) invalidate(qc, "workflows", "detail", workflowId, "runs");
}

// Handles truncated envelope branch: only invalidates the specific event-detail key.
function handleTruncated(qc: QueryClient, runId: string, refEventId: string, frame: Frame): void {
  invalidate(qc, "workflows", "run", runId, "events", refEventId);
  workflowFrameBus.publish(runId, frame);
}

// Standard run event handler: invalidate run + events, publish to bus.
function handleRunEvent(qc: QueryClient, frame: Frame): void {
  const data = frame.data as Record<string, unknown> | null;
  const runId = getRunId(data);
  if (!runId) return;
  if (data?.truncated === true && typeof data.ref_event_id === "string") {
    handleTruncated(qc, runId, data.ref_event_id, frame);
    return;
  }
  invalidateRun(qc, runId);
  invalidateWorkflowRuns(qc, data);
  workflowFrameBus.publish(runId, frame);
}

// Terminal run event: also clears all token delta seq state for the run.
function handleTerminalRunEvent(qc: QueryClient, frame: Frame): void {
  const data = frame.data as Record<string, unknown> | null;
  const runId = getRunId(data);
  if (!runId) return;
  if (data?.truncated === true && typeof data.ref_event_id === "string") {
    handleTruncated(qc, runId, data.ref_event_id, frame);
    return;
  }
  clearTokenDeltaSeqForRun(runId);
  invalidateRun(qc, runId);
  invalidateWorkflowRuns(qc, data);
  workflowFrameBus.publish(runId, frame);
}

// ── Run lifecycle (7) ─────────────────────────────────────────────────────────

export function handleWorkflowRunQueued(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowRunStarted(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowRunCompleted(frame: Frame, qc: QueryClient): void {
  handleTerminalRunEvent(qc, frame);
}

export function handleWorkflowRunFailed(frame: Frame, qc: QueryClient): void {
  handleTerminalRunEvent(qc, frame);
}

export function handleWorkflowRunCancelled(frame: Frame, qc: QueryClient): void {
  handleTerminalRunEvent(qc, frame);
}

export function handleWorkflowRunPaused(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowRunResumed(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

// ── Maintenance / cost run events (4) ─────────────────────────────────────────

export function handleWorkflowRunCostAborted(frame: Frame, qc: QueryClient): void {
  handleTerminalRunEvent(qc, frame);
}

export function handleWorkflowRunReaped(frame: Frame, qc: QueryClient): void {
  handleTerminalRunEvent(qc, frame);
}

export function handleWorkflowRunPoisonPill(frame: Frame, qc: QueryClient): void {
  handleTerminalRunEvent(qc, frame);
}

export function handleWorkflowRunRetried(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

// ── Step lifecycle (5) ────────────────────────────────────────────────────────

export function handleWorkflowStepStarted(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowStepCompleted(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowStepFailed(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowStepSkipped(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowStepProgress(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

// ── Agentic (5) ───────────────────────────────────────────────────────────────

export function handleWorkflowStepTextComplete(frame: Frame, qc: QueryClient): void {
  const data = frame.data as Record<string, unknown> | null;
  const runId = getRunId(data);
  if (!runId) return;
  // Clear live token seq for this step — final text delivered.
  const stepId = typeof data?.step_id === "string" ? data.step_id : null;
  if (stepId) clearTokenDeltaSeqForStep(runId, stepId);
  if (data?.truncated === true && typeof data.ref_event_id === "string") {
    handleTruncated(qc, runId, data.ref_event_id, frame);
    return;
  }
  invalidateRun(qc, runId);
  workflowFrameBus.publish(runId, frame);
}

export function handleWorkflowTokenDelta(frame: Frame, _qc: QueryClient): void {
  // token_delta: NO RQ invalidate (too noisy under firehose).
  // Seq-based dedup: drop frame if seq <= lastSeq[runId:stepId].
  // Q1 LOCKED: Replayable=false, ring-skipped; bus-only delivery.
  const data = frame.data as Record<string, unknown> | null;
  const runId = getRunId(data);
  if (!runId) return;
  const stepId = typeof data?.step_id === "string" ? data.step_id : null;
  if (!stepId) return;

  const rawSeq = data?.seq;
  if (rawSeq !== undefined && rawSeq !== null) {
    // BE emits seq as JSON number; cap at Number.MAX_SAFE_INTEGER before BigInt.
    const seqNum = typeof rawSeq === "number"
      ? (rawSeq > Number.MAX_SAFE_INTEGER ? BigInt(Number.MAX_SAFE_INTEGER) : BigInt(Math.floor(rawSeq)))
      : null;
    if (seqNum !== null) {
      const key = getStepKey(runId, stepId);
      const prev = lastSeq.get(key);
      if (prev !== undefined && seqNum <= prev) {
        // Out-of-order or duplicate — drop frame.
        return;
      }
      evictTokenDeltaSeqIfNeeded();
      lastSeq.set(key, seqNum);
    }
  }

  workflowFrameBus.publish(runId, frame);
}

export function handleWorkflowAgentDecisionMade(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowAgentToolCallStart(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowAgentToolCallResult(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

// ── Checkpoint (7) ────────────────────────────────────────────────────────────

export function handleWorkflowCheckpointCreated(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowCheckpointRequested(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowCheckpointApproved(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowCheckpointRejected(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowCheckpointExpired(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowCheckpointCancelled(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

export function handleWorkflowCheckpointResumeFailed(frame: Frame, qc: QueryClient): void {
  handleRunEvent(qc, frame);
}

// ── Test helper — expose lastSeq for assertion ────────────────────────────────

export function _getLastSeqForTests(): Map<string, bigint> {
  return lastSeq;
}
